import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, QrCode, FlaskConical, Download, Upload, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PoolsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [poolCount, setPoolCount] = useState({ count: 0, limit: 4, can_add_more: true });
  const [uploadingVGB, setUploadingVGB] = useState(null);
  const [formData, setFormData] = useState({
    property_name: '',
    pool_name: '',
    address: '',
    contact_info: '',
    volume: ''
  });

  useEffect(() => {
    fetchPools();
    fetchPoolCount();
  }, []);

  const fetchPools = async () => {
    try {
      const response = await axios.get(`${API}/pools`);
      setPools(response.data);
    } catch (error) {
      console.error('Failed to fetch pools:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPoolCount = async () => {
    try {
      const response = await axios.get(`${API}/pools/count`);
      setPoolCount(response.data);
    } catch (error) {
      console.error('Failed to fetch pool count:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPool) {
        await axios.put(`${API}/pools/${editingPool.id}`, formData);
        toast.success(t('saved'));
      } else {
        await axios.post(`${API}/pools`, formData);
        toast.success(t('saved'));
      }
      setDialogOpen(false);
      resetForm();
      fetchPools();
      fetchPoolCount();
    } catch (error) {
      console.error('Failed to save pool:', error);
      const errorMessage = error.response?.data?.detail || t('error');
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (poolId) => {
    if (!window.confirm('Are you sure you want to delete this pool?')) return;
    
    try {
      await axios.delete(`${API}/pools/${poolId}`);
      toast.success(t('deleted'));
      fetchPools();
      fetchPoolCount();
    } catch (error) {
      console.error('Failed to delete pool:', error);
      toast.error(t('error'));
    }
  };

  const handleEdit = (pool) => {
    setEditingPool(pool);
    setFormData({
      property_name: pool.property_name,
      pool_name: pool.pool_name,
      address: pool.address,
      contact_info: pool.contact_info,
      volume: pool.volume
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPool(null);
    setFormData({
      property_name: '',
      pool_name: '',
      address: '',
      contact_info: '',
      volume: ''
    });
  };

  const handleExport = async (poolId) => {
    try {
      const response = await axios.post(`${API}/export/spreadsheet?pool_id=${poolId}`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pool_compliance_${poolId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Spreadsheet downloaded');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error(t('error'));
    }
  };

  const handleExportMyData = async () => {
    try {
      toast.info('Preparing your data export...');
      const response = await axios.get(`${API}/export/my-data`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `my_aquapro_data_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Your data exported successfully!');
    } catch (error) {
      console.error('Failed to export my data:', error);
      toast.error('Failed to export data');
    }
  };

  const handleVGBUpload = async (poolId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingVGB(poolId);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/pools/${poolId}/vgb-compliance`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('VGB Compliance document uploaded');
      fetchPools();
    } catch (error) {
      console.error('Failed to upload VGB:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingVGB(null);
    }
  };

  const handleVGBDownload = async (poolId) => {
    try {
      const response = await axios.get(`${API}/pools/${poolId}/vgb-compliance`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `VGB_Compliance_${poolId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('VGB Compliance document downloaded');
    } catch (error) {
      console.error('Failed to download VGB:', error);
      toast.error(error.response?.data?.detail || 'Download failed');
    }
  };

  const handleVGBDelete = async (poolId) => {
    if (!window.confirm('Are you sure you want to delete the VGB Compliance document?')) return;

    try {
      await axios.delete(`${API}/pools/${poolId}/vgb-compliance`);
      toast.success('VGB Compliance document deleted');
      fetchPools();
    } catch (error) {
      console.error('Failed to delete VGB:', error);
      toast.error(t('error'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="pools-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-secondary tracking-tight">{t('pools')}</h1>
          <p className="text-muted-foreground mt-2">
            Manage pool profiles and compliance
            {poolCount.limit && (
              <span className="ml-2 text-sm font-medium">
                ({poolCount.count} of {poolCount.limit} pools used)
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportMyData}
            className="flex items-center gap-2"
            data-testid="export-my-data"
          >
            <Download className="h-4 w-4" />
            Export My Data
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-pool-dialog-button" 
              className="flex items-center space-x-2"
              disabled={!poolCount.can_add_more}
            >
              <Plus className="h-4 w-4" />
              <span>{poolCount.can_add_more ? t('addPool') : 'Pool Limit Reached'}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPool ? t('editPool') : t('addPool')}</DialogTitle>
              <DialogDescription>Enter pool information below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="property_name">{t('propertyName')}</Label>
                <Input
                  id="property_name"
                  data-testid="property-name-input"
                  value={formData.property_name}
                  onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pool_name">{t('poolName')}</Label>
                <Input
                  id="pool_name"
                  data-testid="pool-name-input"
                  value={formData.pool_name}
                  onChange={(e) => setFormData({ ...formData, pool_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{t('address')}</Label>
                <Input
                  id="address"
                  data-testid="address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_info">{t('contactInfo')}</Label>
                <Input
                  id="contact_info"
                  data-testid="contact-info-input"
                  value={formData.contact_info}
                  onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="volume">{t('volume')}</Label>
                <Input
                  id="volume"
                  data-testid="volume-input"
                  value={formData.volume}
                  onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                  placeholder="e.g., 50,000 gallons"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <Button type="submit" data-testid="pool-submit-button" className="flex-1">{t('save')}</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  {t('cancel')}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pool Usage Card */}
      {poolCount.limit && (
        <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-secondary">Your Pool Allocation</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {poolCount.can_add_more 
                    ? `You can add ${poolCount.limit - poolCount.count} more pool${poolCount.limit - poolCount.count !== 1 ? 's' : ''}`
                    : 'You have reached your pool limit'}
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold font-mono text-primary">
                  {poolCount.count}/{poolCount.limit}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Pools Used</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  poolCount.count >= poolCount.limit ? 'bg-red-500' : 'bg-primary'
                }`}
                style={{ width: `${(poolCount.count / poolCount.limit) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {pools.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground text-center">{t('noData')}</p>
            <Button 
              onClick={() => setDialogOpen(true)} 
              className="mt-4"
              disabled={!poolCount.can_add_more}
            >
              {poolCount.can_add_more ? t('addPool') : 'Pool Limit Reached'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pools.map((pool) => (
            <Card key={pool.id} data-testid={`pool-card-${pool.id}`} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <CardTitle className="text-xl">{pool.pool_name}</CardTitle>
                <CardDescription>{pool.property_name}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Address:</span> {pool.address}</p>
                  <p><span className="font-medium">Contact:</span> {pool.contact_info}</p>
                  <p><span className="font-medium">Volume:</span> {pool.volume}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid={`view-readings-${pool.id}`}
                    onClick={() => navigate(`/pools/${pool.id}/readings`)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <FlaskConical className="h-4 w-4 mr-2" />
                    {t('readings')}
                  </Button>
                  <Button
                    data-testid={`qr-code-${pool.id}`}
                    onClick={() => navigate(`/pools/${pool.id}/qr`)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    QR
                  </Button>
                </div>
                {/* VGB Compliance Section */}
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">VGB Compliance</p>
                  {pool.vgb_compliance_filename ? (
                    <div className="flex items-center space-x-2">
                      <Button
                        data-testid={`view-vgb-${pool.id}`}
                        onClick={() => handleVGBDownload(pool.id)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        View PDF
                      </Button>
                      <Button
                        data-testid={`delete-vgb-${pool.id}`}
                        onClick={() => handleVGBDelete(pool.id)}
                        variant="outline"
                        size="sm"
                        className="text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="w-full">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => handleVGBUpload(pool.id, e)}
                        className="hidden"
                        data-testid={`upload-vgb-${pool.id}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={uploadingVGB === pool.id}
                        onClick={() => document.querySelector(`input[data-testid="upload-vgb-${pool.id}"]`).click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingVGB === pool.id ? 'Uploading...' : 'Upload PDF'}
                      </Button>
                    </label>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Button
                    data-testid={`export-${pool.id}`}
                    onClick={() => handleExport(pool.id)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={`edit-pool-${pool.id}`}
                    onClick={() => handleEdit(pool)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={`delete-pool-${pool.id}`}
                    onClick={() => handleDelete(pool.id)}
                    variant="outline"
                    size="sm"
                    className="w-full text-danger hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PoolsPage;